import router from '@/router'
import store from '@/store'
import { getCountry } from '@/utils/location/phoneCodeCountries'
import useJwt from '@/auth/jwt/useJwt'
import defaultAvatar from '@/assets/images/profile/profile-user.svg'
import viber from '@/assets/images/icons/viber-48.png'
import whatsapp from '@/assets/images/icons/whatsapp-48.png'
import telegram from '@/assets/images/icons/telegram-48.png'

import { computed, ref } from '@vue/composition-api'
import * as locationService from '@/utils/location/location'
import vueCookie from 'vue-cookie'
import { initialAbility } from '@/libs/acl/config'
import { formatDateTimeUTC } from '@/filters/dateTime'
import { GlobalEventEmitter } from '@/utils/GlobalEventEmitter'
import ability from '@/libs/acl/ability'
import { useLocationRemoteData } from '@/views/apps/location/useLocation'
import { useServicesQueryHandler } from '@/views/apps/service/usePoints'
import axios from '@axios'
import { clearUserData } from '@/views/apps/user/clearUserData'
import { normalizeCoords } from '@/utils/location/geoDistance'
import { storage } from '@/utils/storage'

const MANAGED_HEADER_KEY = 'Managed-User-id'
const ROUTE_FROM_KEY = 'routeFrom'
export const useUserUi = () => {
  const isUserModeServiceProvider = user => !!user?.services?.includes('service_provider')

  // Helper: normalize offers and return only published ones
  const normalizeOfferStatus = offer => String(
    offer?.post_status
      || offer?.status
      || '',
    ).toLowerCase()

  const getPublishedOffers = offers => {
    if (!Array.isArray(offers)) return []

    return offers.filter(offer => offer && normalizeOfferStatus(offer) === 'published')
  }

  const uniqueOffersById = offers => {
    const used = new Set()

    return offers.filter(offer => {
      if (!offer || offer.id === null || offer.id === undefined) return false

      const id = String(offer.id)

      if (used.has(id)) return false

      used.add(id)

      return true
    })
  }

  const getUserServiceOffers = user => {
    const currentUser = store.getters['account/user'] || null
    const targetUser = user || currentUser

    const userOffers = Array.isArray(targetUser?.service_offers)
      ? targetUser.service_offers
      : []

    const storeUserOffers = Array.isArray(store.getters['point/userOffers'])
      ? store.getters['point/userOffers']
      : []

    return uniqueOffersById([
      ...storeUserOffers,
      ...userOffers,
    ])
  }

  const getUserPublishedOffersCount = user => getPublishedOffers(getUserServiceOffers(user)).length

  const isUserHasPublishedOffers = user => getUserPublishedOffersCount(user) > 0

  // Is user a "service provider" from UI perspective
  // (by mode OR by having offers)
  const isUserServiceProvider = user => {
    const currentUser = store.getters['account/user'] || null
    const targetUser = user || currentUser
    if (!targetUser) return false

    const byMode = isUserModeServiceProvider(targetUser)
    const byOffers = isUserHasPublishedOffers(targetUser)

    return byMode || byOffers
  }

  function isUserProcessed(user) {
    return user.processed_at ? formatDateTimeUTC(user.processed_at, 'DD.MM.YYYY') : ''
  }

  function isUserBlocked(user) {
    return user.blocked_at ? formatDateTimeUTC(user.blocked_at, 'DD.MM.YYYY') : ''
  }

  const handleProviderViewActionClick = user => {
    // const route = router.resolve({ name: 'pages-profile-service-provider', params: { id: user.id } })
    // window.open(route.href, '_blank')
    router.push({ name: 'pages-profile-service-provider', params: { id: user.id } })
  }

  const handleReferralViewActionClick = user => {
    // const route = router.resolve({ name: 'pages-profile-service-provider', params: { id: user.id } })
    // window.open(route.href, '_blank')
    router.push({ name: 'pages-profile-referral', params: { id: user.id } })
  }

  const fetchMessengers = () => store.dispatch('appOptions/fetchMessengers').then(response => {
    const successStatuses = [200, 201]
    if (successStatuses.includes(response.status)) {
      const messengers = response.data.data
      messengers.forEach(el => {
        if (el.slug === 'viber' && !el.icon) {
          el.icon = viber
          el.name = 'Viber'
        }
        if (el.slug === 'telegram' && !el.icon) {
          el.icon = telegram
          el.name = 'Telegram'
        }
        if (el.slug === 'whatsapp' && !el.icon) {
          el.icon = whatsapp
          el.name = 'WhatsApp'
        }
      })
      store.commit('appOptions/INIT_MESSENGERS_LIST', messengers)
    }
  })

  const optionMessengers = computed(() => {
    const messengers = store.getters['appOptions/messengers']
    if (!messengers.length) {
      fetchMessengers().then(() => messengers)
    }
    return messengers
  })

  const phoneVisibilityOptions = [
    {
      label: 'privacy.provider_contacts_protected_active_proposal_only',
      value: 'active_proposal',
      icon: 'ShieldIcon',
      variant: 'primary',
      description: 'privacy.provider_contacts_protected_active_proposal_only_desc',
      expanded: 'landing.features.freelancers.privacy.expanded',
      ability: { action: 'show_on_acceptance', subject: 'Contact' },
    },
    {
      label: 'privacy.provider_contacts_protected_authorized_users_only',
      value: 'authorized',
      icon: 'KeyIcon',
      variant: 'primary',
      description: 'privacy.provider_contacts_protected_authorized_users_only_desc',
      expanded: '',
      ability: { action: 'show_auth_users', subject: 'Contact' },
    },
    {
      label: 'privacy.provider_contacts_not_protected_anybody',
      value: 'anybody',
      icon: 'EyeIcon',
      variant: 'primary',
      description: 'privacy.provider_contacts_not_protected_anybody_desc',
      expanded: '',
      ability: { action: 'show_public', subject: 'Contact' },
    },
  ]

  const getPhoneVisibilityOption = visibility => phoneVisibilityOptions.find(el => el.value === visibility) || phoneVisibilityOptions[2]

  const getAvatar = user => user.avatar || defaultAvatar

  const setAuthHeaderManagedUser = userId => {
    if (!userId) return
    // axios default headers shape varies by version; handle both safely
    axios.defaults.headers = axios.defaults.headers || {}
    axios.defaults.headers.common = axios.defaults.headers.common || axios.defaults.headers
    axios.defaults.headers.common[MANAGED_HEADER_KEY] = userId
  }

  const deleteAuthHeaderManagedUser = () => {
    try {
      const h = axios?.defaults?.headers
      if (!h) return
      if (h.common && h.common[MANAGED_HEADER_KEY]) delete h.common[MANAGED_HEADER_KEY]
      if (h[MANAGED_HEADER_KEY]) delete h[MANAGED_HEADER_KEY]
    } catch (_) {}
  }

  const getManagedUserId = () => {
    const managedUser = store.getters['user/userManaged']
    return managedUser ? managedUser.referral_id || managedUser.id : null
  }

  const isManagedUser = user => (user && user.name !== 'Deleted'
    ? user.managed
    : getManagedUserId())

  const safeParseJSON = (raw, fallback = null) => {
    if (!raw) return fallback
    try {
      return JSON.parse(raw)
    } catch (_) {
      return fallback
    }
  }

  const isRouteObjectValid = route => route && typeof route.name === 'string'

  const setLatestUserRoute = () => {
    const { name, params, query } = router.currentRoute
    if (!name) return

    const data = JSON.stringify({ name, params, query })
    localStorage.setItem(ROUTE_FROM_KEY, data)
  }

  const navigateSafely = async location => {
    try {
      await router.push(location)
    } catch (err) {
      // Ignore expected navigation errors (redirects / duplicates)
      if (
        !err
        || !err.message
        || (
          !err.message.includes('NavigationDuplicated')
          && !err.message.includes('Redirected when going from')
        )
      ) {
        // Unexpected errors still get logged
        // eslint-disable-next-line no-console
        console.error('navigateSafely error:', err)
      }
    }
  }

  const fetchCurrentUser = () => store.dispatch('account/fetchUserCurrent')

  const clearNavbarUserOffers = () => {
    try {
      if (store._mutations && store._mutations['point/resetUserOffers']) {
        store.commit('point/resetUserOffers')
        return
      }

      store.commit('point/setUserOffers', [])
    } catch (_) {}
  }

  const normalizeUserId = user => user?.referral_id || user?.id || null

  // --- MANAGE USER (switch into managed user) ---
  const manageUser = async (user, route) => {
    const managedId = normalizeUserId(user)
    if (!managedId) {
      throw new Error('manageUser: missing user id')
    }

    const redirectTo = route || { name: 'pages-account-setting' }

    setAuthHeaderManagedUser(managedId)
    store.commit('account/setSwitching', true)

    try {
      clearNavbarUserOffers()
      await store.dispatch('user/setUserManaged', user)
      const response = await fetchCurrentUser()
      await store.dispatch('wallet/fetchWallet')
      await navigateSafely(redirectTo)
      return response
    } finally {
      store.commit('account/setSwitching', false)
    }
  }

  // --- UNMANAGE USER (switch back to owner) ---
  const unManageUser = async () => {
    store.commit('account/setSwitching', true)

    clearUserData()
    clearNavbarUserOffers()

    try {
      const response = await fetchCurrentUser()

      await store.dispatch('wallet/fetchWallet')

      const routeFrom = safeParseJSON(localStorage.getItem(ROUTE_FROM_KEY))
      if (
        isRouteObjectValid(routeFrom)
        && routeFrom.name !== router.currentRoute.name
      ) {
        await navigateSafely({
          name: routeFrom.name,
          params: routeFrom.params || {},
          query: routeFrom.query || {},
        })
      }

      return response
    } finally {
      store.commit('account/setSwitching', false)
    }
  }

  // --- PUBLIC API: switchToUser ---
  const switchToUser = async (user, route) => {
    // If user passed → switch INTO managed user
    if (user) {
      setLatestUserRoute()
      return manageUser(user, route)
    }

    // If no user passed → switch BACK to owner
    return unManageUser()
  }

  async function logout() {
    try { sessionStorage.setItem('auth.logoutInProgress', '1') } catch (_) {}

    try {
      // navigate first
      const navPromise = (async () => {
        try {
          await router.replace({ name: 'auth', query: { form: 'login' } })
        } catch (_) {}
      })()

      useJwt.logoutUser()

      // clear headers on @axios (make sure this is @axios not axios package)
      try {
        axios.defaults.headers = axios.defaults.headers || {}
        axios.defaults.headers.common = axios.defaults.headers.common || {}
        delete axios.defaults.headers.common.Authorization
        delete axios.defaults.headers.common[MANAGED_HEADER_KEY]
        delete axios.defaults.headers.Authorization
        delete axios.defaults.headers[MANAGED_HEADER_KEY]
      } catch (_) {}

      clearUserData()

      // cookies best effort
      const tokenKey = useJwt.jwtConfig.storageTokenKeyName
      const refreshKey = useJwt.jwtConfig.storageRefreshTokenKeyName
      try {
        if (typeof vueCookie.delete === 'function') {
          vueCookie.delete(tokenKey, { path: '/' })
          vueCookie.delete(refreshKey, { path: '/' })
        } else if (typeof vueCookie.remove === 'function') {
          vueCookie.remove(tokenKey, { path: '/' })
          vueCookie.remove(refreshKey, { path: '/' })
        }
      } catch (_) {}

      try { ability.update(initialAbility) } catch (_) {}

      await navPromise
    } finally {
      try { sessionStorage.removeItem('auth.logoutInProgress') } catch (_) {}
    }
  }

  return {
    isUserServiceProvider,
    isUserHasPublishedOffers,
    isUserModeServiceProvider,
    getUserPublishedOffersCount,
    getUserServiceOffers,
    switchToUser,
    setAuthHeaderManagedUser,
    deleteAuthHeaderManagedUser,
    getManagedUserId,
    isManagedUser,
    isUserProcessed,
    isUserBlocked,
    handleProviderViewActionClick,
    handleReferralViewActionClick,
    getAvatar,
    optionMessengers,
    logout,
    phoneVisibilityOptions,
    getPhoneVisibilityOption,
  }
}

export const useUserLocation = () => {
  const getUserGeoData = computed(() => store.getters['user/geoData'])

  const GEO_CACHE_KEY = 'user-geo-data'
  const GEO_CACHE_META_KEY = 'user-geo-data-meta'
  const GEO_INIT_LOCK_KEY = 'user-geo-init-lock-until'
  const GEO_ENRICH_LOCK_KEY = 'user-geo-enrich-lock-until'

  const GEO_CACHE_TTL_MS = 6 * 60 * 60 * 1000 // 6 hours
  const GEO_ENRICH_TTL_MS = 24 * 60 * 60 * 1000 // 24 hours
  const GEO_INIT_LOCK_TTL_MS = 15 * 1000 // 15 seconds
  const GEO_ENRICH_LOCK_TTL_MS = 30 * 1000 // 30 seconds

  const userCountryCode = computed(() => {
    const geoData = getUserGeoData.value
    const countryCode = geoData?.country_code || geoData?.address?.country_code

    return countryCode ? String(countryCode).toUpperCase() : 'UA'
  })

  function setUserCountryCode(code) {
    // Keep existing un-prefixed key behavior for backwards compatibility.
    localStorage.setItem('dial_code', code)
  }

  const getUserCountry = () => {
    const country = getCountry(userCountryCode.value)

    return country ? country.name : ''
  }

  const getUserLat = computed(() => normalizeCoords(getUserGeoData.value)?.lat ?? null)

  const getUserLng = computed(() => normalizeCoords(getUserGeoData.value)?.lng ?? null)

  const getNow = () => Date.now()

  const readGeoCacheMeta = () => storage.get(GEO_CACHE_META_KEY, {}) || {}

  const writeGeoCacheMeta = meta => {
    storage.set(GEO_CACHE_META_KEY, {
      ...readGeoCacheMeta(),
      ...meta,
    })
  }

  const getLockUntil = key => Number(storage.getItem(key) || 0)

  const isLocked = key => getLockUntil(key) > getNow()

  const setLock = (key, ttlMs) => {
    storage.setItem(key, String(getNow() + ttlMs))
  }

  const clearLock = key => {
    storage.removeItem(key)
  }

  const hasGeoCoords = geoData => !!normalizeCoords(geoData)

  const hasDisplayAddress = geoData => {
    const normalizedGeoData = locationService.normalizeGeoData(geoData)

    if (!normalizedGeoData?.address) return false

    const { address } = normalizedGeoData

    return !!(
      address.province
      || address.locality
      || address.city
      || address.town
      || address.village
      || address.municipality
      || address.suburb
    )
  }

  const readGeoCache = () => {
    const cachedGeoData = storage.get(GEO_CACHE_KEY, null)
    const meta = readGeoCacheMeta()
    const cachedAt = Number(meta.cachedAt || 0)

    if (!cachedGeoData || !cachedAt) return null
    if (getNow() - cachedAt > GEO_CACHE_TTL_MS) return null
    if (!hasGeoCoords(cachedGeoData)) return null

    return cachedGeoData
  }

  const writeGeoCache = (geoData, meta = {}) => {
    const normalizedGeoData = locationService.normalizeGeoData(geoData)

    if (!normalizedGeoData) return null

    storage.set(GEO_CACHE_KEY, normalizedGeoData)
    writeGeoCacheMeta({
      ...meta,
      cachedAt: getNow(),
    })

    return normalizedGeoData
  }

  const clearUserGeoCache = () => {
    storage.removeItem(GEO_CACHE_KEY)
    storage.removeItem(GEO_CACHE_META_KEY)
    storage.removeItem(GEO_INIT_LOCK_KEY)
    storage.removeItem(GEO_ENRICH_LOCK_KEY)
  }

  const runWhenIdle = callback => {
    if (typeof window === 'undefined') return

    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(callback, { timeout: 3000 })
      return
    }

    window.setTimeout(callback, 0)
  }

  const shouldEnrichGeoData = (geoData, force = false) => {
    if (force) return true
    if (!hasGeoCoords(geoData)) return false
    if (hasDisplayAddress(geoData)) return false

    const meta = readGeoCacheMeta()
    const enrichedAt = Number(meta.enrichedAt || 0)

    return getNow() - enrichedAt > GEO_ENRICH_TTL_MS
  }

  const updateUserGeoData = geoData => {
    const normalizedGeoData = locationService.normalizeGeoData(geoData)

    if (!normalizedGeoData) return null

    store.commit('user/updateGeoData', normalizedGeoData)

    return normalizedGeoData
  }

  const normalizeAddressPartForCompare = value => String(value || '')
    .trim()
    .toLocaleLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[.,;:()[\]{}'"`’‘“”\-_/\\]+/g, ' ')
    .replace(/\s+/g, ' ')

  const isSameAddressPart = (a, b) => {
    const left = normalizeAddressPartForCompare(a)
    const right = normalizeAddressPartForCompare(b)

    return !!left && !!right && left === right
  }

  const uniqueAddressParts = parts => {
    const used = new Set()

    return parts
      .map(part => String(part || '').trim())
      .filter(Boolean)
      .filter(part => {
        const key = normalizeAddressPartForCompare(part)

        if (!key || used.has(key)) return false

        used.add(key)

        return true
      })
  }

  const firstAddressPart = (...parts) => parts.find(part => String(part || '').trim()) || ''

  const getLocalityAddressPart = address => firstAddressPart(
    address.locality,
    address.city,
    address.town,
    address.village,
    address.municipality,
    address.suburb,
  )

  const getRegionAddressPart = (address, locality) => {
    const candidates = [
      address.province,
      address.state,
      address.state_district,
      address.county,
      address.borough,
      address.city_district,
      address.suburb,
    ]

    return candidates.find(part => {
      if (!part) return false

      return !isSameAddressPart(part, locality)
    }) || ''
  }

  const getUserAddressFormattedShort = computed(() => {
    const { transformFetchedLocality } = useLocationRemoteData()
    const geoData = locationService.normalizeGeoData(getUserGeoData.value)

    if (!geoData?.address) return ''

    const { address } = geoData

    const country = address.country
      ? String(address.country).trim().toUpperCase()
      : ''

    const city = transformFetchedLocality(geoData)
      || address.locality
      || address.city
      || address.town
      || address.village
      || address.municipality
      || ''

    return [country, city]
      .map(part => String(part || '').trim())
      .filter(Boolean)
      .filter((part, index, list) => (
        list.findIndex(item => item.toLocaleLowerCase() === part.toLocaleLowerCase()) === index
      ))
      .join(', ')
  })

  const enrichUserGeoAddress = async (geoData = null, options = {}) => {
    const { force = false } = options
    const sourceGeoData = geoData || getUserGeoData.value
    const normalizedSourceGeoData = locationService.requireGeoData(sourceGeoData)

    if (!shouldEnrichGeoData(normalizedSourceGeoData, force)) {
      return normalizedSourceGeoData
    }

    if (!force && isLocked(GEO_ENRICH_LOCK_KEY)) {
      return normalizedSourceGeoData
    }

    setLock(GEO_ENRICH_LOCK_KEY, GEO_ENRICH_LOCK_TTL_MS)

    try {
      const enrichedGeoData = await locationService.locationByCoordinates(
        normalizedSourceGeoData,
        normalizedSourceGeoData.country_code || userCountryCode.value,
      )

      const normalizedGeoData = updateUserGeoData(enrichedGeoData)

      if (normalizedGeoData) {
        writeGeoCache(normalizedGeoData, {
          enrichedAt: getNow(),
        })
      }

      return normalizedGeoData || normalizedSourceGeoData
    } finally {
      clearLock(GEO_ENRICH_LOCK_KEY)
    }
  }

  const fetchUserGeoDataFromIp = async () => {
    const response = await store.dispatch('user/fetchGeoData')
    const fetchedGeoData = locationService.requireGeoDataFromResponse(response)
    const normalizedGeoData = updateUserGeoData(fetchedGeoData)

    if (normalizedGeoData) {
      writeGeoCache(normalizedGeoData, {
        fetchedAt: getNow(),
      })
    }

    return normalizedGeoData
  }

  const initUserCountryData = async (options = {}) => {
    const {
      force = false,
      enrichAddress = true,
      waitForEnrichment = false,
    } = options

    if (!force) {
      const cachedGeoData = readGeoCache()

      if (cachedGeoData) {
        const normalizedCachedGeoData = updateUserGeoData(cachedGeoData)

        if (enrichAddress && shouldEnrichGeoData(normalizedCachedGeoData)) {
          if (waitForEnrichment) {
            await enrichUserGeoAddress(normalizedCachedGeoData).catch(() => null)
          } else {
            runWhenIdle(() => {
              enrichUserGeoAddress(normalizedCachedGeoData).catch(() => {})
            })
          }
        }

        return normalizedCachedGeoData
      }

      if (isLocked(GEO_INIT_LOCK_KEY)) {
        return getUserGeoData.value
      }
    }

    setLock(GEO_INIT_LOCK_KEY, GEO_INIT_LOCK_TTL_MS)

    try {
      const normalizedGeoData = await fetchUserGeoDataFromIp()

      if (enrichAddress && shouldEnrichGeoData(normalizedGeoData)) {
        if (waitForEnrichment) {
          const enrichedGeoData = await enrichUserGeoAddress(normalizedGeoData).catch(() => null)

          return enrichedGeoData || normalizedGeoData
        }

        runWhenIdle(() => {
          enrichUserGeoAddress(normalizedGeoData).catch(() => {})
        })
      }

      return normalizedGeoData
    } finally {
      clearLock(GEO_INIT_LOCK_KEY)
    }
  }

  const setUserCountryData = async () => {
    const response = await store.dispatch('user/fetchGeoData')
    const fetchedGeoData = locationService.requireGeoDataFromResponse(response)
    const geoData = await locationService.locationByCoordinates(fetchedGeoData, userCountryCode.value)
    const normalizedGeoData = updateUserGeoData(geoData)

    if (normalizedGeoData) {
      writeGeoCache(normalizedGeoData, {
        fetchedAt: getNow(),
        enrichedAt: getNow(),
      })
    }

    return normalizedGeoData
  }

  const updatingUserCountryData = ref(false)

  const updateUserCountryData = async (lat, lng) => {
    updatingUserCountryData.value = true
    const countryCode = userCountryCode.value

    const normalizeGeoError = error => {
      const msg = error?.message || 'Geolocation error'

      if (msg === 'User denied Geolocation') return 'Please provide geolocation permission'

      return msg
    }

    try {
      const coords = normalizeCoords({ lat, lng })

      const geoData = coords
        ? await locationService.locationByCoordinates(coords, countryCode)
        : await locationService.currentLocation(countryCode)

      const normalizedGeoData = updateUserGeoData(locationService.requireGeoData(geoData))

      if (normalizedGeoData) {
        writeGeoCache(normalizedGeoData, {
          enrichedAt: getNow(),
          detectedAt: getNow(),
        })
      }

      return normalizedGeoData
    } catch (error) {
      const message = normalizeGeoError(error)

      GlobalEventEmitter.$emit(
        'showCustomToast',
        { status: 'error', statusText: message },
        'Geolocation',
        null,
        null,
        4000,
      )

      error.message = message
      throw error
    } finally {
      updatingUserCountryData.value = false
    }
  }

  return {
    userCountryCode,
    getUserLat,
    getUserLng,
    updateUserGeoData,
    getUserAddressFormattedShort,
    setUserCountryData,
    initUserCountryData,
    enrichUserGeoAddress,
    clearUserGeoCache,
    updateUserCountryData,
    updatingUserCountryData,
    setUserCountryCode,
    getUserGeoData,
  }
}
export const useUserQueryHandler = () => {
  const userQueryKey = 'uid'
  const { SERVICE_QUERY_PAGE_KEY } = useServicesQueryHandler()

  const getServiceProviderProfileLink = user => {
    const userId = user.referral_id || user.id
    if (userId) {
      const path = `${window.location.origin}/${SERVICE_QUERY_PAGE_KEY}`
      const url = new URL(path)
      const params = url.searchParams
      params.append(userQueryKey, userId)
      return url.toString()
    }

    return ''
  }
  return {
    getServiceProviderProfileLink,
  }
}
